import { vueEnv } from "@/config/vueEnv";
import { clearAuthStorage, loadStoredToken, useSessionCookie, usesBearerFallback } from "@/config/session";

/**
 * Single backend origin for fetch calls.
 * Local: testOpsHub/.env (VUE_APP_API_URL). Deployed: same-origin /api unless VUE_APP_API_URL is set.
 *
 * Dev: webpack devServer proxies /api → backend (see vue.config.js).
 * Prod: nginx proxies /api → BACKEND_UPSTREAM (see gitops values + entrypoint.sh).
 */
const configuredApiUrl = vueEnv("VUE_APP_API_URL");

/** Value from .env before browser-only resolution (for debug). */
export const CONFIGURED_API_URL = configuredApiUrl;

const DEFAULT_DEV = "http://localhost:3000";

/** True if URL uses Kubernetes in-cluster DNS (browser outside the cluster cannot resolve it). */
export function isClusterInternalApiUrl(url) {
  if (!url || typeof url !== "string") return false;
  return /\.svc\.cluster\.local/i.test(url) || /\.cluster\.local(\/|:|$)/i.test(url);
}

function isWebpackDev() {
  return (
    typeof process !== "undefined" &&
    process.env &&
    process.env.NODE_ENV !== "production"
  );
}

function resolveApiBase() {
  // Dev server proxies /api → backend (see vue.config.js). Same-origin works for
  // localhost and LAN IPs (e.g. http://192.168.1.105:8095).
  if (isWebpackDev() && typeof window !== "undefined") {
    return "";
  }
  if (CONFIGURED_API_URL) {
    if (typeof window !== "undefined" && isClusterInternalApiUrl(CONFIGURED_API_URL)) {
      return "";
    }
    return CONFIGURED_API_URL;
  }
  // Deployed build with no VUE_APP_API_URL → same-origin /api (nginx → backend pod).
  if (typeof window !== "undefined" && !isWebpackDev()) {
    return "";
  }
  if (typeof window !== "undefined") {
    const host = (window.location?.hostname || "").toLowerCase();
    if (host && host !== "localhost" && host !== "127.0.0.1") {
      return `http://${host}:3000`;
    }
  }
  return DEFAULT_DEV;
}

const resolvedBase = resolveApiBase();

export const RAW_VUE_APP_API_URL = CONFIGURED_API_URL;
export const API_BASE_URL = resolvedBase;

function describeSource() {
  if (configuredApiUrl) {
    if (typeof window !== "undefined" && window.ENV?.VUE_APP_API_URL) {
      return "window.ENV.VUE_APP_API_URL";
    }
    return "process.env.VUE_APP_API_URL (.env)";
  }
  if (typeof window !== "undefined" && !isWebpackDev()) {
    return "same-origin /api (nginx → backend)";
  }
  return "fallback:http://localhost:3000";
}

/** Short explanation for misconfigured VUE_APP_API_URL in GitOps. */
export function clusterInternalApiUrlHelp() {
  return (
    "VUE_APP_API_URL points to in-cluster DNS (*.svc.cluster.local). " +
    "Browsers cannot resolve that. Set it to a public / ingress URL for the API (e.g. http(s)://api… on cmp.int.lightnet.io), or add an nginx proxy on this host for /api."
  );
}

export const API_BASE_SOURCE =
  typeof window !== "undefined" && isClusterInternalApiUrl(CONFIGURED_API_URL)
    ? `${describeSource()} → same-origin /api (in-cluster URL not usable in browser)`
    : describeSource();

const DEFAULT_FETCH_TIMEOUT_MS = 30000;
/** AI scope / report generation can take minutes (S3 + Jira + Gemini). */
export const AI_FETCH_TIMEOUT_MS = 180000;

/** True when the UI is served from the webpack dev server. */
function isLocalDevEnvironment() {
  return isWebpackDev();
}

function formatApiEndpointForError() {
  if (API_BASE_URL) return API_BASE_URL;
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin} (proxied /api → port 3000)`;
  }
  return "the API";
}

function timeoutErrorMessage() {
  const base = formatApiEndpointForError();
  if (isLocalDevEnvironment()) {
    return (
      `Request timed out. The API may not be running at ${base}. ` +
      'Start the backend with "yarn dev" (one instance on port 3000).'
    );
  }
  return `Request timed out while contacting the API (${base}). Please try again later.`;
}

function unreachableApiErrorMessage() {
  const base = formatApiEndpointForError();
  if (isLocalDevEnvironment()) {
    return (
      `Cannot reach the API (${base}). ` +
      'Ensure the backend is running (e.g. "yarn dev" on port 3000).'
    );
  }
  return (
    `Cannot reach the API (${base}). ` +
    "Check your network connection or contact your administrator if the problem persists."
  );
}

/** Attach auth — cookie session in prod; Bearer only when cookie fallback is active. */
export function apiHeaders(extra = {}) {
  const headers = { ...extra };
  if (!useSessionCookie() || usesBearerFallback()) {
    const token = loadStoredToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }
  return headers;
}

function mergeFetchOptions(options = {}) {
  const merged = {
    ...options,
    headers: {
      ...apiHeaders(),
      ...(options.headers || {}),
    },
  };
  if (useSessionCookie()) {
    merged.credentials = options.credentials || "include";
  }
  return merged;
}

/**
 * fetch with AbortController timeout so login/forms do not spin forever when API is down.
 * Automatically sends session cookie (prod) or Authorization Bearer (local dev).
 */
export async function fetchWithTimeout(url, options = {}, timeoutMs = DEFAULT_FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...mergeFetchOptions(options), signal: controller.signal });
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error(timeoutErrorMessage());
    }
    if (err?.message === "Failed to fetch") {
      throw new Error(unreachableApiErrorMessage());
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}
